import { Fragment, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import CelestialBackground from "@/components/CelestialBackground";
import SiteMenu from "@/components/SiteMenu";
import AdminMenu from "@/components/AdminMenu";
import { Login } from "@/pages/Analytics";
import { money, cardCls, btnGold } from "@/lib/creators";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "astra_analytics_token";
const FILTERS = ["", "pending", "approved", "rejected", "suspended"];
const pill = "px-3 py-1.5 rounded-full text-xs border border-[#D4AF37]/30 hover:border-[#D4AF37] transition-colors disabled:opacity-50";

function Activity({ id, h, reload }) {
  const [a, setA] = useState(null);
  const load = useCallback(() => axios.get(`${API}/admin/creators/${id}/activity`, h).then(({ data }) => setA(data)), [id, h]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (cid, status) => {
    try { await axios.post(`${API}/admin/conversions/${cid}/status`, { status }, h); toast.success(`Sale ${status === "void" ? "voided" : status}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't update this sale"); }
    load(); reload();
  };
  if (!a) return <Loader2 className="animate-spin gold-text" size={18} />;
  return (
    <div className="grid gap-4 lg:grid-cols-2 text-sm" data-testid={`creator-activity-${id}`}>
      <div>
        <p className="gold-text mb-1">Sales ({a.conversions.length})</p>
        {a.conversions.length === 0 && <p className="text-[#A7B0C0]">None yet.</p>}
        {a.conversions.map((x) => (
          <div key={x.id} className="flex flex-wrap items-center gap-2 py-1.5 border-b border-[#D4AF37]/10">
            <span className="flex-1">{x.productType} · {money(x.orderValue, x.currency)} → <b className="gold-text">{money(x.commissionAmount, x.currency)}</b> <span className="text-[#A7B0C0]">{new Date(x.createdAt).toLocaleDateString()}</span></span>
            <span className="text-xs uppercase text-[#A7B0C0]">{x.status}</span>
            {x.status === "pending" && <button className={pill} data-testid={`conv-approve-${x.id}`} onClick={() => setStatus(x.id, "approved")}>Approve</button>}
            {x.status !== "paid" && x.status !== "void" && <button className={pill} data-testid={`conv-void-${x.id}`} onClick={() => setStatus(x.id, "void")}>Void</button>}
          </div>
        ))}
      </div>
      <div>
        <p className="gold-text mb-1">Recent clicks ({a.clicks.length})</p>
        {a.clicks.slice(0, 15).map((x) => <p key={x.id} className="text-[#A7B0C0]">{new Date(x.createdAt).toLocaleString()} · {x.landingPage || "/"}</p>)}
      </div>
    </div>
  );
}

function SampleButton({ token }) {
  const [id, setId] = useState("");
  const [busy, setBusy] = useState(false);
  const [approved, setApproved] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/creators`, { params: { status: "approved" }, headers: { "X-Analytics-Token": token } }).then(({ data }) => setApproved(data.creators)).catch(() => {});
  }, [token]);
  const send = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/admin/creators/sample-weekly`, { creator_id: id || null }, { headers: { "X-Analytics-Token": token } }); toast.success(`2 sample emails sent to ${data.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't send the sample"); }
    setBusy(false);
  };
  return (
    <span className="flex items-center gap-1.5">
      {approved.length > 0 && (
        <select data-testid="sample-creator" value={id} onChange={(e) => setId(e.target.value)} className="bg-[#0B0F19] border border-[#D4AF37]/25 rounded-full px-3 py-1.5 text-sm">
          <option value="">Top creator</option>{approved.map((c) => <option key={c.id} value={c.id}>@{c.handle}</option>)}
        </select>
      )}
      <button data-testid="send-sample-weekly" onClick={send} disabled={busy} className={`${pill} text-sm`}>{busy ? <Loader2 size={14} className="animate-spin" /> : "Send me a sample"}</button>
    </span>
  );
}

function Row({ c, h, reload }) {
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(Math.round(c.commissionRate * 100));
  const [later, setLater] = useState(Math.round((c.laterRate ?? 0.2) * 100));
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    setBusy(false);
  };
  const patch = (body, ok) => run(() => axios.patch(`${API}/admin/creators/${c.id}`, body, h), ok);
  const app = c.application || {};
  return (
    <Fragment>
      <tr className="border-t border-[#D4AF37]/10 align-top" data-testid={`creator-row-${c.handle}`}>
        <td className="py-3 pr-3"><button onClick={() => setOpen(!open)} className="text-left"><span className="block text-starlight">{c.displayName}</span><span className="text-xs gold-text">@{c.handle} · {c.refCode}</span><span className="block text-xs text-[#A7B0C0]">{c.email}</span></button></td>
        <td className="py-3 pr-3 text-xs text-[#A7B0C0]">{c.niche}<br />{(c.platforms || []).join(", ")}<br />{(app.audienceSize || 0).toLocaleString()} followers</td>
        <td className="py-3 pr-3"><span data-testid={`creator-status-${c.handle}`} className="text-xs uppercase tracking-wider">{c.status}</span></td>
        <td className="py-3 pr-3 text-xs">{c.totalClicks} clicks<br />{c.totalConversions} sales<br />{Object.entries(c.earnedByCurrency || {}).map(([cur, v]) => money(v, cur)).join(" + ") || money(0)}</td>
        <td className="py-3 pr-3 text-xs">Owed {money(c.owed.approved)}<br /><span className="text-[#A7B0C0]">Pending {money(c.owed.pending)}</span><br />{c.payoutsEnabled ? "Stripe ready" : "No Stripe yet"}</td>
        <td className="py-3">
          <div className="flex flex-wrap gap-1.5">
            {c.status !== "approved" && <button disabled={busy} className={pill} data-testid={`approve-${c.handle}`} onClick={() => patch({ status: "approved" }, "Approved and emailed")}>Approve</button>}
            {c.status === "pending" && <button disabled={busy} className={pill} data-testid={`reject-${c.handle}`} onClick={() => patch({ status: "rejected" }, "Rejected and emailed")}>Reject</button>}
            {c.status === "approved" && <button disabled={busy} className={pill} data-testid={`suspend-${c.handle}`} onClick={() => patch({ status: "suspended", notify: false }, "Suspended")}>Suspend</button>}
            {c.status === "approved" && <button disabled={busy} className={pill} data-testid={`resend-${c.handle}`} onClick={() => run(() => axios.post(`${API}/admin/creators/${c.id}/send-approval`, {}, h), "Approval email sent")}>Send approval email</button>}
            {c.owed.approved > 0 && <button disabled={busy} className={pill} data-testid={`payout-${c.handle}`} onClick={() => run(() => axios.post(`${API}/admin/creators/${c.id}/payout`, {}, h), "Paid through Stripe")}>Pay via Stripe</button>}
            {c.owed.approved > 0 && <button disabled={busy} className={pill} data-testid={`mark-paid-${c.handle}`} onClick={() => run(() => axios.post(`${API}/admin/creators/${c.id}/mark-paid`, {}, h), "Marked paid")}>Mark paid</button>}
          </div>
          <div className="flex items-center gap-1.5 mt-2 text-xs">
            <input data-testid={`rate-${c.handle}`} type="number" min={0} max={90} value={rate} onChange={(e) => setRate(e.target.value)} className="w-14 bg-[#0B0F19] border border-[#D4AF37]/25 rounded px-2 py-1" />% then
            <input data-testid={`later-rate-${c.handle}`} type="number" min={0} max={90} value={later} onChange={(e) => setLater(e.target.value)} className="w-14 bg-[#0B0F19] border border-[#D4AF37]/25 rounded px-2 py-1" />%
            <button disabled={busy} className={pill} data-testid={`save-rate-${c.handle}`} onClick={() => patch({ commissionRate: rate / 100, laterRate: later / 100 }, "Rates saved")}>Save</button>
          </div>
        </td>
      </tr>
      {open && (
        <tr><td colSpan={6} className="pb-4">
          <div className="bg-[#0B0F19] rounded-xl p-4 space-y-3">
            {app.whyAstra && <p className="text-sm text-[#D6D0C5]"><span className="gold-text">Why Astra:</span> {app.whyAstra}</p>}
            {(app.links || []).length > 0 && <p className="text-sm text-[#A7B0C0]">Links: {app.links.join(" · ")}</p>}
            <Activity id={c.id} h={h} reload={reload} />
          </div>
        </td></tr>
      )}
    </Fragment>
  );
}

export function CreatorsPanel({ token, onExpired, title = "Creators" }) {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const h = { headers: { "X-Analytics-Token": token } };
  const load = useCallback(() => {
    axios.get(`${API}/admin/creators`, { params: { status: status || undefined }, headers: { "X-Analytics-Token": token } })
      .then(({ data }) => setRows(data.creators))
      .catch((e) => { if (e.response?.status === 401) onExpired(); });
  }, [status, token, onExpired]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = async () => {
    const { data } = await axios.get(`${API}/admin/creators/export.csv`, { headers: { "X-Analytics-Token": token }, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data); a.download = "astra-creators.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };
  return (
    <>
      <header className="flex flex-wrap items-center gap-3 mb-6">
        {title && <div><p className="text-[11px] uppercase tracking-[0.3em] gold-text">Astra · Private</p><h1 className="text-3xl font-light">{title}</h1></div>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => <button key={f || "all"} data-testid={`filter-${f || "all"}`} onClick={() => setStatus(f)}
            className={`px-3 py-1.5 rounded-full text-sm ${status === f ? "bg-[#D4AF37] text-[#04060B]" : "border border-[#D4AF37]/30 text-[#A7B0C0]"}`}>{f || "all"}</button>)}
          <SampleButton token={token} />
          <button data-testid="export-csv" onClick={exportCsv} className={`${btnGold} inline-flex items-center gap-1.5 px-4 py-1.5 text-sm`}><Download size={14} /> CSV</button>
        </div>
      </header>
      <div className={`${cardCls} overflow-x-auto`}>
        {!rows ? <Loader2 className="animate-spin gold-text" /> : rows.length === 0 ? <p data-testid="admin-creators-empty" className="text-[#A7B0C0]">No creators with this status.</p> : (
          <table className="w-full text-sm min-w-[900px]">
            <thead><tr className="text-left text-[11px] uppercase tracking-[0.2em] text-[#A7B0C0]"><th className="pb-2">Creator</th><th>Audience</th><th>Status</th><th>Totals</th><th>Payout</th><th>Actions</th></tr></thead>
            <tbody>{rows.map((c) => <Row key={c.id} c={c} h={h} reload={load} />)}</tbody>
          </table>
        )}
      </div>
    </>
  );
}

export function AdminShell({ testId, children }) {
  return (
    <div className="app-shell wide-desktop relative text-starlight">
      <CelestialBackground />
      <SiteMenu floating />
      <AdminMenu />
      <div className="relative z-10 px-5 pt-16 pb-16 lg:max-w-7xl lg:mx-auto" data-testid={testId}>{children}</div>
    </div>
  );
}

export default function AdminCreators() {
  const [token, setToken] = useState(() => sessionStorage.getItem(KEY));
  const expire = useCallback(() => { sessionStorage.removeItem(KEY); setToken(null); }, []);
  return (
    <AdminShell testId="admin-creators">
      {!token ? <Login onToken={setToken} /> : (
        <>
          <button data-testid="admin-logout" onClick={expire} className="absolute right-16 top-5 text-[#A7B0C0]" aria-label="Sign out"><LogOut size={16} /></button>
          <CreatorsPanel token={token} onExpired={expire} />
        </>
      )}
    </AdminShell>
  );
}
